import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';

type ButtonVariant = 'primary' | 'outline' | 'light';

type BaseButtonProps = {
  children: ReactNode;
  variant?: ButtonVariant;
  className?: string;
};

type ButtonAsButtonProps = BaseButtonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: never;
  };

type ButtonAsLinkProps = BaseButtonProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & {
    href: string;
  };

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const variantClassNames: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 shadow-soft focus-visible:ring-brand-300',
  outline:
    'border border-brand-300 text-brand-700 bg-white/95 hover:bg-brand-50 active:bg-brand-100 focus-visible:ring-brand-200',
  light:
    'bg-white text-brand-700 hover:bg-brand-50 active:bg-brand-100 shadow-soft focus-visible:ring-brand-200'
};

export function Button({ children, variant = 'primary', className = '', ...props }: ButtonProps) {
  const classNames = `inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 ${variantClassNames[variant]} ${className}`;

  if ('href' in props) {
    const { href, ...anchorProps } = props as ButtonAsLinkProps;
    return (
      <a href={href} className={classNames} {...anchorProps}>
        {children}
      </a>
    );
  }

  const buttonProps = props as ButtonAsButtonProps;

  return (
    <button
      className={classNames}
      {...buttonProps}
    >
      {children}
    </button>
  );
}
